/* eslint-disable max-classes-per-file */
// this is a registery of errors that performFetch could raise, there is no reason
// to restrict it to one-class-per-file

import { Optional } from "common/utils/types";

import {
  FieldErrors,
  FetchError as FetchErrorType,
  StatusCode,
  FatalFetchErrorKind,
} from "./types";

/** {@inheritDoc fetch/types!FetchError} */
export class FetchError<T = object, D = object> extends Error implements FetchErrorType<T, D> {
  constructor(
    public status: number = StatusCode.InternalServerError,

    public detail: string = "Something went wrong.",
    public errors: FieldErrors<T> = {},
    public data: Optional<D> = null,
  ) {
    super(JSON.stringify(errors));
    this.status = status;
    this.detail = detail;
    this.errors = errors;
    this.data = data;
  }
}

/**
 * Fetch errors that are expected to be handled by fetch infrastructure, and tend to indicate
 * that something has gone wrong that can't be recovered from without user interaction, or a
 * page-reload.
 */
export class FatalFetchError<T = object> extends FetchError<T> {
  constructor(
    public kind: FatalFetchErrorKind,
    status: number,
    detail: string,
  ) {
    super(status, detail, {}, {});
  }
}

/** Copy shown when a search-backed request fails because Elasticsearch is unavailable. */
export const SEARCH_UNAVAILABLE_TITLE = "Search is temporarily unavailable";
export const SEARCH_UNAVAILABLE_DESCRIPTION =
  "We've been notified and are working to restore it. Please try again in a few moments.";

/**
 * True when a request failed because Elasticsearch is unavailable. The backend returns a 503
 * flagged with `is_search_unavailable` (see core.exceptions.SearchUnavailableError) so search
 * surfaces can show a friendly degraded state instead of a generic error.
 */
export const isSearchUnavailableError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") {
    return false;
  }
  const { status, errors } = error as Partial<FetchErrorType>;
  return (
    status === StatusCode.ServiceUnavailable &&
    (errors as { is_search_unavailable?: boolean } | undefined)?.is_search_unavailable === true
  );
};
